import { useState, useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Search, Plus, Trash2, Package, Sparkles, Boxes } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { formatMoney } from '@/shared/utils/currency';
import { useProductSearch } from '@/features/pos/hooks/usePosApi';
import { useProducts, type ProductHierarchyItem } from '@/features/catalog/hooks/useCatalogApi';
import { ProductHierarchySelector } from '@/features/catalog/components/ProductHierarchySelector';
import { UnitPackCell } from '@/features/catalog/components/UnitPackCell';
import { convertRateForUnit } from '@/shared/utils/unitConversion';
import { abbreviatePackaging, derivePackagingType } from '@/shared/utils/packagingAbbreviation';
import { ErrorBoundary } from '@/shared/components/feedback/ErrorBoundary';
import { packSummary, totalBaseQuantityLabel, piecesPerPack, baseUnitDivisor, ratePerBaseUnit } from '@/shared/utils/ratePerBaseUnit';
import type { PipelineLine } from '../types';

export function calculateLineTotals(line: PipelineLine) {
  const qty = Number(line.quantity) || 0;
  const rate = Number(line.unitPrice) || 0;
  const disc = Number(line.discount) || 0;
  const rawDiscountAmount = line.discountType === 'fixed'
    ? disc
    : (qty * rate * Math.min(100, disc)) / 100;
  const discountAmount = Math.min(Math.max(0, rawDiscountAmount), qty * rate);

  const taxable = Math.max(0, qty * rate - discountAmount);
  // A tax profile stores BOTH halves of the same 18%: the intra-state pair
  // (cgst 9 + sgst 9) AND the inter-state igst 18. Adding all three charged
  // 36%, so a pipeline record showed a total half again as large as the
  // quotation raised from it. Prefer the pair; fall back to igst when a line
  // only carries that. Same rule as gstRateOf() in the quotation form.
  const gstPair = (Number(line.cgstRate) || 0) + (Number(line.sgstRate) || 0);
  const taxRate = gstPair > 0 ? gstPair : (Number(line.igstRate) || 0);
  const taxAmount = (taxable * taxRate) / 100;
  const lineTotal = taxable + taxAmount;

  return {
    gross: qty * rate,
    discountAmount,
    taxable,
    taxRate,
    taxAmount,
    lineTotal: Math.round(lineTotal * 100) / 100,
  };
}

/**
 * The price of ONE PIECE inside a multi-piece pack (e.g. one 900 ML bottle
 * out of a 20-bottle box), derived from the stored per-pack unitPrice —
 * never the other way around. `unitPrice` stays the single number every
 * consumer of this line (calculateLineTotals above, Dispatch's own loading
 * math, the saved document) already reads as "price of one Qty-unit"; this
 * is a pure display/entry convenience layered on top, not an alternate
 * meaning for the stored field.
 */
// piecesPerPack/baseUnitDivisor now live in shared/utils/ratePerBaseUnit.ts
// (imported above) so the Quotation form's Rate cell can offer the same
// per-piece/per-KG/per-LTR entry modes without duplicating this logic.

/**
 * A line's OWN total GST rate, independent of how it happens to be split right
 * now — same "prefer the pair" extraction calculateLineTotals uses, exposed so
 * a line can be RE-split when intraState changes (e.g. the record's contact is
 * swapped for one in a different state) rather than only computed once at
 * add-time and then frozen even after the correct split has changed.
 */
export function totalGstRateOf(line: PipelineLine): number {
  const pair = (Number(line.cgstRate) || 0) + (Number(line.sgstRate) || 0);
  return pair > 0 ? pair : (Number(line.igstRate) || 0);
}

/**
 * Split a line's total GST rate into the pair the server will actually sum
 * (computeLine() in shared/documents/totals.js adds cgstRate + sgstRate +
 * igstRate — safe only when exactly one arrangement is populated). Intra-state
 * carries CGST+SGST half each; inter-state carries the whole rate as IGST.
 */
export function splitGst(rate: number, intraState: boolean): { cgstRate: number; sgstRate: number; igstRate: number } {
  return intraState
    ? { cgstRate: rate / 2, sgstRate: rate / 2, igstRate: 0 }
    : { cgstRate: 0, sgstRate: 0, igstRate: rate };
}

/** Re-split every line for a (possibly new) intraState — see splitGst(). Used
 *  when the record's contact changes state after lines were already added. */
export function resplitLinesForSupplyType(lines: PipelineLine[], intraState: boolean): PipelineLine[] {
  return lines.map((l) => ({ ...l, ...splitGst(totalGstRateOf(l), intraState) }));
}

export function calculateAllLinesTotals(lines: PipelineLine[]) {
  let subTotal = 0;
  let discountTotal = 0;
  let taxTotal = 0;
  let grandTotal = 0;
  let totalQty = 0;

  for (const line of lines) {
    const t = calculateLineTotals(line);
    subTotal += t.gross;
    discountTotal += t.discountAmount;
    taxTotal += t.taxAmount;
    grandTotal += t.lineTotal;
    totalQty += Number(line.quantity) || 0;
  }

  return {
    totalQty,
    subTotal: Math.round(subTotal * 100) / 100,
    discountTotal: Math.round(discountTotal * 100) / 100,
    taxTotal: Math.round(taxTotal * 100) / 100,
    grandTotal: Math.round(grandTotal * 100) / 100,
  };
}

export function ProductLinesEditor({
  lines,
  onChange,
  readOnly = false,
  discountType = 'percent',
  onDiscountTypeChange,
  // Defaults to intra-state — the same assumption Quotations make
  // (buyerStateCodeForQuotation in shared/sales/bill-to.js) when the
  // contact's place of supply can't yet be resolved, e.g. no contact picked
  // yet. Pass the resolved value (useBillToPreview(contactId).intraState)
  // once a contact is selected so a new line is split correctly from the
  // start rather than needing a re-split immediately after.
  intraState = true,
  // The record's currency: a USD record's rates and totals are in dollars.
  currency = 'INR',
}: {
  currency?: string;
  lines: PipelineLine[];
  onChange: (lines: PipelineLine[]) => void;
  readOnly?: boolean;
  intraState?: boolean;
  discountType?: 'percent' | 'fixed';
  onDiscountTypeChange?: (type: 'percent' | 'fixed') => void;
}) {
  const money = (n?: number | null) => formatMoney(n, currency);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showHierarchy, setShowHierarchy] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  // Which unit the Rate box is currently being TYPED in, per line index —
  // purely a display/entry convenience (never saved, never read by
  // calculateLineTotals). Defaults to 'pack' so every existing line renders
  // exactly as it always has; a rep only sees the choice at all once a line
  // actually has more than one piece per pack (piecesPerPack(line) > 1).
  const [rateModeByIndex, setRateModeByIndex] = useState<Record<number, 'pack' | 'piece' | 'base'>>({});

  const handleHierarchySelect = (
    item: ProductHierarchyItem,
    qty: number,
    customRate?: number
  ) => {
    const rate = customRate !== undefined ? customRate : (item.sellingPrice || item.mrp || 0);
    // The hierarchy API resolves a real, correctly-defaulted rate per product
    // (src/api-catalog/products.js) as a CGST+SGST pair — prefer it, falling
    // back to igst only when the pair is genuinely absent, same rule as
    // gstRateOf() in the quotation form.
    const itemGstRate = (Number(item.cgstRate) || 0) + (Number(item.sgstRate) || 0) || (Number(item.igstRate) || 0);
    const newLine: PipelineLine = {
      productId: item._id,
      productName: item.name,
      grade: item.grade,
      description: '',
      productCode: item.sku,
      capacity: `${item.packSize} (${item.packagingType})`,
      hsnSac: item.hsnSac || '',
      quantity: qty,
      unit: abbreviatePackaging(derivePackagingType(item)),
      unitId: null,
      packSize: item.packSize || '',
      qtyPerPack: Number(item.qtyPerPack) || 0,
      totalPackSize: item.totalPackSize || '',
      volumeLiters: Number(item.volumeLiters) || 0,
      mrp: item.mrp || rate,
      basePrice: item.sellingPrice || rate,
      unitPrice: rate,
      discount: 0,
      discountType,
      // Split for the CURRENT contact's supply type rather than assumed
      // intra-state: a line added for an inter-state contact must carry IGST
      // from the moment it's added, not just after a later re-split.
      ...splitGst(itemGstRate, intraState),
      // Notes stays free text for whatever the rep actually wants to say
      // about this line. It used to also carry "Packaging: X | 900 ML x 20
      // = 18 LTR" here — the exact same pack summary the row already shows
      // on its own dedicated line right below (packSummary(line)) — so
      // every catalogue-picked line said it twice.
      notes: '',
    };

    const existingIndex = lines.findIndex((l) => l.productId && l.productId === item._id);
    if (existingIndex >= 0) {
      const updated = lines.map((l, i) => {
        if (i !== existingIndex) return l;
        const combined = { ...l, quantity: (Number(l.quantity) || 0) + qty };
        if (customRate !== undefined) combined.unitPrice = customRate;
        return { ...combined, lineTotal: calculateLineTotals(combined).lineTotal };
      });
      onChange(updated);
      // Said out loud — a silent merge on a long list looked like "nothing added".
      toast(`${item.name} is already on line ${existingIndex + 1} — its quantity is now ${(Number(lines[existingIndex].quantity) || 0) + qty}.`, { icon: 'ℹ️' });
    } else {
      newLine.lineTotal = calculateLineTotals(newLine).lineTotal;
      onChange([...lines, newLine]);
      toast.success(`Added ${qty} ${item.packagingType}(s) of ${item.name}`);
    }

    setShowHierarchy(false);
  };

  const { data: searchResults = [], isLoading: isSearchLoading } = useProductSearch(searchQuery);
  // Before anything is typed, the dropdown browses the catalog instead of
  // sitting empty — clicking the box is what starts the search, not typing.
  const { data: browseData, isLoading: isBrowseLoading } = useProducts({ limit: 30 });
  const isBrowsing = searchQuery.trim().length === 0;
  const results = isBrowsing ? (browseData?.data ?? []) : searchResults;
  const resultsLoading = isBrowsing ? isBrowseLoading : isSearchLoading;

  // Close search dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearching(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addProductToLines = (product: any) => {
    const mrp = Number(product.mrp || product.sellingPriceIncTax || product.sellingPrice || 0);
    const basePrice = Number(product.sellingPrice || product.purchasePrice || 0);
    const unitPrice = basePrice || mrp || 0;
    
    // Determine capacity / packaging format
    let capacity = '';
    if (product.packSizes && product.packSizes.length > 0) {
      capacity = product.packSizes[0].label || '';
    } else if (product.volumeLiters) {
      capacity = `${product.volumeLiters} L`;
    } else if (product.weightKg) {
      capacity = `${product.weightKg} kg`;
    }

    // Packaging first — a product's real pack (Box/Drum/Barrel/Bucket/Can) is
    // what the catalogue actually stores per-product, while unitId is a
    // rarely-set generic UoM reference (ML/LTR/KG/GM). Same rule already
    // applied to handleHierarchySelect below — this is the OTHER product-add
    // path (flat search, not the Division/Group/Grade hierarchy picker),
    // which still defaulted every line's Unit to the generic ML/LTR/KG/GM
    // master instead of the product's real container.
    const unitName = abbreviatePackaging(derivePackagingType(product)) || product.unitId?.shortName || product.unitId?.name || 'LTR';
    // A product with no taxProfileId linked falls back to the standard 18%
    // GST rate — not silent 0%, which is what an unlinked product used to
    // produce here and is a real compliance gap on a taxable sale, not a
    // discount. Split for the CURRENT contact's supply type (see splitGst),
    // not assumed intra-state, so an inter-state contact gets IGST from the
    // moment the line is added.
    const taxProfile = product.taxProfileId || null;
    const productGstRate = taxProfile
      ? ((Number(taxProfile.cgstRate) || 0) + (Number(taxProfile.sgstRate) || 0) || (Number(taxProfile.igstRate) || 0))
      : 18;
    const { cgstRate, sgstRate, igstRate } = splitGst(productGstRate, intraState);

    const newLine: PipelineLine = {
      productId: product._id,
      productName: product.name || '',
      grade: product.grade || '',
      description: '',
      productCode: product.sku || '',
      capacity,
      packSize: product.packSize || capacity,
      qtyPerPack: Number(product.qtyPerPack) || 0,
      totalPackSize: product.totalPackSize || '',
      volumeLiters: Number(product.volumeLiters) || 0,
      hsnSac: product.hsnSac || '',
      quantity: 1,
      unit: unitName,
      unitId: product.unitId?._id || product.unitId || null,
      mrp,
      basePrice,
      unitPrice,
      discount: 0,
      discountType,
      cgstRate,
      sgstRate,
      igstRate,
      notes: product.description || '',
    };

    // Picking a product already on the list adds to it rather than opening a
    // second line for the same thing — the same behaviour the reusable
    // LineItemsEditor (quotations/orders) already has.
    const existingIndex = lines.findIndex((l) => l.productId && l.productId === product._id);
    if (existingIndex >= 0) {
      const updated = lines.map((l, i) => {
        if (i !== existingIndex) return l;
        const combined = { ...l, quantity: (Number(l.quantity) || 0) + 1 };
        return { ...combined, lineTotal: calculateLineTotals(combined).lineTotal };
      });
      onChange(updated);
      toast(`${product.name} is already on line ${existingIndex + 1} — its quantity is now ${(Number(lines[existingIndex].quantity) || 0) + 1}.`, { icon: 'ℹ️' });
      setSearchQuery('');
      setIsSearching(false);
      return;
    }

    const computed = calculateLineTotals(newLine);
    newLine.lineTotal = computed.lineTotal;

    onChange([...lines, newLine]);
    setSearchQuery('');
    setIsSearching(false);
  };

  const updateLine = (index: number, patch: Partial<PipelineLine>) => {
    const updated = lines.map((l, i) => {
      if (i !== index) return l;
      const combined = { ...l, ...patch };
      const totals = calculateLineTotals(combined);
      return { ...combined, lineTotal: totals.lineTotal };
    });
    onChange(updated);
  };

  const removeLine = (index: number) => {
    onChange(lines.filter((_, i) => i !== index));
  };

  const totals = calculateAllLinesTotals(lines);

  // Read-only presentation
  if (readOnly) {
    if (lines.length === 0) {
      return (
        <div className="rounded-xl border border-outline bg-surface-variant/30 p-6 text-center text-body-sm text-on-surface-variant">
          No product requirements specified for this record.
        </div>
      );
    }
    return (
      <div className="overflow-hidden rounded-xl border border-outline bg-surface">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-left text-body-sm">
            <thead className="border-b border-outline bg-surface-variant/60 text-body-xs font-semibold uppercase tracking-wider text-on-surface-variant">
              <tr>
                <th className="px-4 py-3">Product & Details</th>
                <th className="px-3 py-3">HSN</th>
                <th className="px-3 py-3 text-right">Qty</th>
                <th className="px-3 py-3">Unit</th>
                <th className="px-3 py-3 text-right">MRP</th>
                <th className="px-3 py-3 text-right">Base Price</th>
                <th className="px-3 py-3 text-right">Quoted Rate</th>
                <th className="px-3 py-3 text-right">Disc</th>
                <th className="px-4 py-3 text-right">Line Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline">
              {lines.map((line, i) => (
                <tr key={i} className="hover:bg-surface-variant/30 transition">
                  <td className="px-4 py-3">
                    <p className="font-medium text-on-surface">
                      {line.productName || line.description || line.productCode || 'Custom Line Item'}
                    </p>
                    {line.notes && <p className="text-body-xs text-on-surface-variant line-clamp-1">{line.notes}</p>}
                    {packSummary(line) && <p className="text-data-xs font-semibold text-success-text">{packSummary(line)}</p>}
                  </td>
                  <td className="px-3 py-3 text-on-surface-variant">{line.hsnSac || '—'}</td>
                  <td className="px-3 py-3 text-right font-semibold text-on-surface">
                    {line.quantity}
                    {totalBaseQuantityLabel(line.quantity, line) && (
                      <span className="block text-data-xs text-success-text">{totalBaseQuantityLabel(line.quantity, line)}</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-on-surface-variant">{line.unit || '—'}</td>
                  <td className="px-3 py-3 text-right text-on-surface-variant">{line.mrp ? money(line.mrp) : '—'}</td>
                  <td className="px-3 py-3 text-right text-on-surface-variant">{line.basePrice ? money(line.basePrice) : '—'}</td>
                  <td className="px-3 py-3 text-right font-medium text-on-surface">
                    {money(line.unitPrice)}
                    {piecesPerPack(line) > 1 && (
                      <span className="block text-data-xs font-normal text-on-surface-variant">
                        {money((Number(line.unitPrice) || 0) / piecesPerPack(line))} / piece
                      </span>
                    )}
                    {baseUnitDivisor(line) && (
                      <span className="block text-data-xs font-normal text-on-surface-variant">
                        {money((Number(line.unitPrice) || 0) / (baseUnitDivisor(line)!.qty))} / {baseUnitDivisor(line)!.unit}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right text-on-surface-variant">
                    {line.discount
                      ? (line.discountType === 'fixed' ? money(line.discount) : `${line.discount}%`)
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-on-surface">{money(line.lineTotal ?? 0)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-outline bg-surface-variant/40">
              <tr>
                <td colSpan={8} className="px-4 py-3 text-right font-semibold text-on-surface">Total Value:</td>
                <td className="px-4 py-3 text-right text-body-md font-bold text-primary tabular-nums">
                  {money(totals.grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* ── Product Search & Quick Add Bar ───────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-stretch gap-3">
        <div ref={searchContainerRef} className="relative flex-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
            <input
              type="text"
              className="h-11 w-full rounded-xl border border-outline bg-surface pl-10 pr-10 text-body-sm text-on-surface placeholder:text-on-surface-variant/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition shadow-xs"
              placeholder="Search product from catalog by name, SKU, or specification…"
              value={searchQuery}
              onFocus={() => setIsSearching(true)}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsSearching(true);
              }}
            />
            {resultsLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}
          </div>

          {/* Product Dropdown — browses the catalog on focus, narrows to a
              search once something is typed. Opens on click, not on typing. */}
          {isSearching && (
            <div className="absolute z-30 mt-1.5 max-h-[30rem] w-full overflow-y-auto rounded-xl border border-outline bg-surface p-1 shadow-xl">
              {isBrowsing && results.length > 0 && (
                <div className="px-3 py-1.5 text-body-xs font-medium text-on-surface-variant">
                  Browse the catalog, or type to search…
                </div>
              )}
              {results.length === 0 && !resultsLoading ? (
                <div className="p-4 text-center text-body-sm text-on-surface-variant">
                  {isBrowsing ? 'No products in the catalog yet.' : `No catalog products found for "${searchQuery}".`}
                  {!isBrowsing && (
                    <div className="mt-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          window.open(`/catalog?create=true&name=${encodeURIComponent(searchQuery.trim())}`, '_blank');
                        }}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" /> Request New Product in Catalog
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                results.slice(0, isBrowsing ? 30 : 10).map((product: any) => {
                  const mrp = product.mrp || product.sellingPriceIncTax || product.sellingPrice || 0;
                  const basePrice = product.sellingPrice || product.purchasePrice || 0;
                  const unitStr = product.unitId?.shortName || product.unitId?.name || 'LTR';
                  return (
                    <button
                      key={product._id}
                      type="button"
                      onClick={() => addProductToLines(product)}
                      className="group flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition hover:bg-surface-variant/80 focus:bg-surface-variant focus:outline-none"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-on-surface text-body-sm group-hover:text-primary transition">
                            {product.name}
                          </span>
                          {product.sku && (
                            <span className="rounded bg-surface-variant px-1.5 py-0.5 text-body-xs font-mono text-on-surface-variant">
                              {product.sku}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-body-xs text-on-surface-variant">
                          {product.categoryId?.name && <span>Category: {product.categoryId.name}</span>}
                          {product.packSizes?.[0]?.label && <span>Pack: {product.packSizes[0].label}</span>}
                          {product.currentStock !== undefined && (
                            <span>Stock: <strong className="text-on-surface">{product.currentStock} {unitStr}</strong></span>
                          )}
                        </div>
                      </div>

                      <div className="shrink-0 text-right">
                        <div className="font-semibold text-body-sm text-on-surface">
                          {money(basePrice)}
                        </div>
                        {mrp > 0 && mrp !== basePrice && (
                          <div className="text-body-xs text-on-surface-variant">
                            MRP: {money(mrp)}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        <Button
          type="button"
          onClick={() => setShowHierarchy(!showHierarchy)}
          className={`shrink-0 h-11 gap-1.5 shadow-xs font-bold ${
            showHierarchy
              ? 'bg-primary text-on-primary ring-2 ring-primary/30'
              : 'bg-surface hover:bg-surface-container-high border border-outline text-primary'
          }`}
        >
          <Boxes className="h-4 w-4" /> {showHierarchy ? 'Hide Product Selector' : 'RBM Product Selector'}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={() => window.open('/catalog?create=true', '_blank')}
          className="shrink-0 h-11 border-dashed text-primary hover:bg-primary/5"
          title="Create or request a new product in Catalog (requires Admin approval for BDMs)"
        >
          <Plus className="mr-1.5 h-4 w-4" /> Add / Request Product
        </Button>
      </div>

      {/* ── Collapsible RBM Product Selector (Division/Group/Grade/Pack Size) ── */}
      {showHierarchy && (
        <div className="animate-fadeIn">
          <ErrorBoundary
            fallback={
              <div className="p-4 rounded-xl border border-danger/30 bg-danger/5 text-danger text-body-sm flex items-center justify-between">
                <span>Product selector encountered an issue loading. You can still use the catalog search bar above or add custom lines.</span>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowHierarchy(false)}>
                  Close
                </Button>
              </div>
            }
          >
            <ProductHierarchySelector
              onSelectProduct={handleHierarchySelect}
              onCancel={() => setShowHierarchy(false)}
            />
          </ErrorBoundary>
        </div>
      )}

      {/* ── Product Lines Table ────────────────────────────────────────── */}
      {lines.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-outline bg-surface-variant/20 p-8 text-center">
          <div className="rounded-full bg-primary/10 p-3 text-primary">
            <Package className="h-6 w-6" />
          </div>
          <h4 className="mt-3 text-body-base font-semibold text-on-surface">No Products Added Yet</h4>
          <p className="mt-1 max-w-md text-body-sm text-on-surface-variant">
            Search for products in the catalog above or add custom lines. Capacity, MRP, and Base Price will be automatically populated.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-border bg-card shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-left text-body-sm">
              <thead className="border-b border-outline bg-surface-variant/50 text-body-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                <tr>
                  <th className="px-3.5 py-3 w-[23%]">Product & Description</th>
                  <th className="px-2.5 py-3 w-[12%]">HSN</th>
                  <th className="px-2 py-3 w-[8%] text-center">Qty</th>
                  <th className="px-2 py-3 w-[8%]">Unit</th>
                  <th className="px-2.5 py-3 w-[10%] text-right">MRP (₹)</th>
                  <th className="px-2.5 py-3 w-[10%] text-right">Base Price (₹)</th>
                  <th className="px-2.5 py-3 w-[11%] text-right">Price / Rate (₹)</th>
                  <th className="px-2.5 py-3 w-[12%] text-right">Disc</th>
                  <th className="px-3.5 py-3 w-[11%] text-right">Line Total (₹)</th>
                  <th className="px-2 py-3 w-[4%] text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline">
                {lines.map((line, index) => {
                  const lineComputed = calculateLineTotals(line);
                  return (
                    <tr key={index} className="hover:bg-surface-variant/20 transition-colors">
                      {/* Product Name & Description */}
                      <td className="px-3.5 py-3 align-top">
                        <div className="flex flex-col gap-1.5">
                          {line.productName ? (
                            // A catalog-added line shows its real name
                            // read-only — description stays empty for it
                            // rather than duplicating the name into a second,
                            // editable field. Grade isn't appended here: the
                            // catalogue's own product name already carries it
                            // (e.g. "RBM BETA 10W40 SM 800 ML"), so a
                            // separate "· Grade: 10W40" was pure repetition.
                            <div className="px-0.5 py-1 text-body-sm font-semibold text-on-surface">
                              {line.productName}
                            </div>
                          ) : (
                            <input
                              type="text"
                              placeholder="Product name or SKU"
                              value={line.description || ''}
                              onChange={(e) => updateLine(index, { description: e.target.value })}
                              className="w-full rounded-lg border border-outline bg-surface px-2.5 py-1.5 text-body-sm font-semibold text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          )}
                          <input
                            type="text"
                            placeholder="Add item notes / specs..."
                            value={line.notes || ''}
                            onChange={(e) => updateLine(index, { notes: e.target.value })}
                            className="w-full rounded-md border border-outline/70 bg-surface/50 px-2 py-1 text-body-xs text-on-surface-variant placeholder:text-on-surface-variant/50 focus:border-primary focus:outline-none"
                          />
                          {packSummary(line) && (
                            <p className="rounded-md bg-success/10 px-2 py-1 text-data-xs font-semibold text-success-text">
                              {packSummary(line)}
                            </p>
                          )}
                        </div>
                      </td>

                      {/* HSN — a snapshot at add time (like productCode/capacity
                          above), so it keeps printing what was actually
                          quoted even if the product's own HSN changes in the
                          catalog later. Replaces the Capacity/Pack column
                          here (and in the read-only view above); `capacity`
                          itself is unchanged on the line, just no longer
                          its own column. */}
                      <td className="px-2.5 py-3 align-top">
                        <input
                          type="text"
                          placeholder="HSN / SAC"
                          value={line.hsnSac || ''}
                          onChange={(e) => updateLine(index, { hsnSac: e.target.value })}
                          className="w-full rounded-lg border border-outline bg-surface px-2.5 py-1.5 text-body-sm text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </td>

                      {/* Quantity */}
                      <td className="px-2 py-3 align-top">
                        <BufferedNumberInput
                          min={0.001}
                          value={line.quantity || 1}
                          onChange={(val) => updateLine(index, { quantity: val || 1 })}
                          className="w-full rounded-lg border border-outline bg-surface px-2 py-1.5 text-center text-body-sm font-semibold text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        {totalBaseQuantityLabel(line.quantity, line) && (
                          <p className="mt-1 text-center text-data-xs font-semibold text-success-text">
                            {totalBaseQuantityLabel(line.quantity, line)}
                          </p>
                        )}
                      </td>

                      {/* Unit — switching it recalculates Rate proportionally
                          (₹200/LTR → ₹0.2/ML), so typing a quantity in
                          whatever unit the customer actually asked for prices
                          correctly without hand conversion. MRP and Base
                          Price move with it for the same reason; only Rate
                          feeds the line total (calculateLineTotals above). */}
                      <td className="px-2 py-3 align-top">
                        <UnitPackCell
                          unit={line.unit || 'LTR'}
                          unitPrice={line.unitPrice}
                          packSize={line.packSize}
                          onChange={({ unit, unitPrice }) => updateLine(index, {
                            unit,
                            unitPrice,
                            mrp: convertRateForUnit(line.unit || 'LTR', unit, line.mrp || 0),
                            basePrice: convertRateForUnit(line.unit || 'LTR', unit, line.basePrice || 0),
                          })}
                        />
                      </td>

                      {/* MRP (Auto-filled) */}
                      <td className="px-2.5 py-3 align-top">
                        <div className="relative">
                          <BufferedNumberInput
                            min={0}
                            placeholder="0.00"
                            value={line.mrp !== undefined && line.mrp !== null ? line.mrp : ''}
                            onChange={(val) => updateLine(index, { mrp: val })}
                            className="w-full rounded-lg border border-outline/80 bg-surface-variant/30 px-2 py-1.5 text-right text-body-sm text-on-surface-variant focus:bg-surface focus:border-primary focus:outline-none"
                          />
                        </div>
                      </td>

                      {/* Base Price (Auto-filled) */}
                      <td className="px-2.5 py-3 align-top">
                        <div className="relative">
                          <BufferedNumberInput
                            min={0}
                            placeholder="0.00"
                            value={line.basePrice !== undefined && line.basePrice !== null ? line.basePrice : ''}
                            onChange={(val) => updateLine(index, { basePrice: val })}
                            className="w-full rounded-lg border border-outline/80 bg-surface-variant/30 px-2 py-1.5 text-right text-body-sm text-on-surface-variant focus:bg-surface focus:border-primary focus:outline-none"
                          />
                        </div>
                      </td>

                      {/* Price / Rate (Selling Price). Always STORES the price
                          of one whole Qty-unit (one pack) — the only number
                          calculateLineTotals ever reads. When a pack holds more
                          than one piece, a rep can switch to typing the price
                          of ONE PIECE instead; that value is converted to the
                          per-pack price the instant it's typed, so unitPrice
                          itself never changes meaning. */}
                      <td className="px-2.5 py-3 align-top">
                        {(() => {
                          const perPack = piecesPerPack(line);
                          const hasMultiPack = perPack > 1;
                          const baseUnit = baseUnitDivisor(line);
                          const hasBaseUnit = !!baseUnit;
                          const mode = (hasMultiPack || hasBaseUnit) ? (rateModeByIndex[index] || 'pack') : 'pack';
                          const divisor = mode === 'piece' ? perPack : mode === 'base' ? (baseUnit?.qty || 1) : 1;
                          const stored = Number(line.unitPrice) || 0;
                          const displayed = mode !== 'pack' ? Math.round((stored / divisor) * 100) / 100 : stored;
                          return (
                            <>
                              <BufferedNumberInput
                                min={0}
                                placeholder="0.00"
                                value={line.unitPrice !== undefined ? displayed : ''}
                                onChange={(typed) => {
                                  updateLine(index, { unitPrice: mode !== 'pack' ? typed * divisor : typed });
                                }}
                                className="w-full rounded-lg border border-outline bg-surface px-2.5 py-1.5 text-right text-body-sm font-bold text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                              />
                              {(hasMultiPack || hasBaseUnit) && (
                                <select
                                  value={mode}
                                  onChange={(e) => setRateModeByIndex((prev) => ({ ...prev, [index]: e.target.value as 'pack' | 'piece' | 'base' }))}
                                  className="mt-1 w-full rounded border-0 bg-transparent text-data-xs font-medium text-on-surface-variant focus:outline-none cursor-pointer"
                                >
                                  <option value="pack">Per {line.unit ? line.unit : 'Pack'}{hasMultiPack ? ` (×${perPack})` : ''}</option>
                                  {hasMultiPack && (
                                    <option value="piece">Per Piece{line.packSize ? ` (${line.packSize})` : ''}</option>
                                  )}
                                  {hasBaseUnit && (
                                    <option value="base">Per {baseUnit.unit} Cost (×{baseUnit.qty})</option>
                                  )}
                                </select>
                              )}
                              {ratePerBaseUnit(line.unitPrice, line) && (
                                <p className="mt-1 text-right text-data-xs font-semibold text-success-text">
                                  {ratePerBaseUnit(line.unitPrice, line)}
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </td>

                      <td className="px-2.5 py-3 align-top">
                        <BufferedNumberInput
                          min={0}
                          max={discountType === 'percent' ? 100 : undefined}
                          value={line.discount ?? ''}
                          onChange={(value) => updateLine(index, {
                            discount: discountType === 'percent' ? Math.min(100, Math.max(0, value || 0)) : Math.max(0, value || 0),
                            discountType,
                          })}
                          className="w-full rounded-lg border border-outline bg-surface px-2 py-1.5 text-right text-body-sm font-semibold focus:border-primary focus:outline-none"
                        />
                        {lineComputed.discountAmount > 0 && <p className="mt-1 text-right text-data-xs text-danger">-{money(lineComputed.discountAmount)}</p>}
                      </td>

                      {/* Calculated Line Total */}
                      <td className="px-3.5 py-3.5 align-top text-right font-bold text-body-sm tabular-nums text-on-surface">
                        {money(lineComputed.lineTotal)}
                      </td>

                      {/* Remove Button */}
                      <td className="px-2 py-3 align-top text-center">
                        <button
                          type="button"
                          onClick={() => removeLine(index)}
                          aria-label="Remove item"
                          className="rounded-lg p-1.5 text-on-surface-variant transition hover:bg-danger/10 hover:text-danger focus:outline-none"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Table Footer Summary ─────────────────────────────────────── */}
          {onDiscountTypeChange && (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-outline bg-surface px-6 py-3">
              <span className="text-body-sm font-semibold text-on-surface">Discount mode for all products</span>
              <div className="flex overflow-hidden rounded-lg border border-outline text-xs font-bold">
                <button type="button" onClick={() => onDiscountTypeChange('percent')} className={`px-3 py-2 ${discountType === 'percent' ? 'bg-primary text-on-primary' : 'bg-surface text-on-surface-variant'}`}>%</button>
                <button type="button" onClick={() => onDiscountTypeChange('fixed')} className={`border-l border-outline px-3 py-2 ${discountType === 'fixed' ? 'bg-primary text-on-primary' : 'bg-surface text-on-surface-variant'}`}>₹</button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-outline bg-surface-variant/40 px-6 py-4">
            <div className="flex items-center gap-2 text-body-xs text-on-surface-variant">
              <Sparkles className="h-4 w-4 text-primary" />
              <span>{lines.length} {lines.length === 1 ? 'product line' : 'product lines'} • Total quantity: <strong>{totals.totalQty}</strong> units</span>
            </div>

            <div className="flex flex-wrap items-center gap-6 text-body-sm">
              <div className="text-on-surface-variant">
                Subtotal: <span className="font-semibold text-on-surface">{money(totals.subTotal)}</span>
              </div>
              {totals.discountTotal > 0 && (
                <div className="text-on-surface-variant">
                  Discount: <span className="font-semibold text-danger">-{money(totals.discountTotal)}</span>
                </div>
              )}
              {totals.taxTotal > 0 && (
                <div className="text-on-surface-variant">
                  Est. GST: <span className="font-semibold text-on-surface">{money(totals.taxTotal)}</span>
                </div>
              )}
              <div className="flex items-center gap-2 rounded-xl bg-primary/10 px-3.5 py-1.5 text-primary">
                <span className="text-body-xs uppercase font-bold tracking-wider">Total Value:</span>
                <span className="text-body-base font-extrabold tabular-nums">{money(totals.grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BufferedNumberInput({
  value,
  onChange,
  className,
  placeholder,
  min = 0,
  max,
}: {
  value: number | string;
  onChange: (val: number) => void;
  className?: string;
  placeholder?: string;
  min?: number;
  max?: number;
}) {
  const [localVal, setLocalVal] = useState(String(value ?? ''));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setLocalVal(value !== undefined && value !== null && value !== '' ? String(value) : '');
    }
  }, [value, isFocused]);

  return (
    <input
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      value={isFocused ? localVal : (value !== undefined && value !== null && value !== '' ? String(value) : '')}
      onFocus={() => {
        setIsFocused(true);
        setLocalVal(value !== undefined && value !== null && value !== '' ? String(value) : '');
      }}
      onBlur={() => {
        setIsFocused(false);
        const trimmed = localVal.trim();
        if (trimmed === '' || trimmed === '.' || trimmed === '-') {
          onChange(0);
          setLocalVal('');
        } else {
          const num = parseFloat(trimmed);
          if (!isNaN(num)) {
            let finalVal = min !== undefined ? Math.max(min, num) : num;
            if (max !== undefined) finalVal = Math.min(max, finalVal);
            onChange(finalVal);
            setLocalVal(String(finalVal));
          } else {
            onChange(0);
            setLocalVal('');
          }
        }
      }}
      onChange={(e) => {
        const next = e.target.value;
        if (/^-?\d*\.?\d*$/.test(next)) {
          setLocalVal(next);
          if (next !== '' && next !== '.' && next !== '-' && !next.endsWith('.')) {
            const num = parseFloat(next);
            if (!isNaN(num)) {
              let finalVal = min !== undefined ? Math.max(min, num) : num;
              if (max !== undefined) finalVal = Math.min(max, finalVal);
              onChange(finalVal);
            }
          }
        }
      }}
      className={className}
    />
  );
}

export default ProductLinesEditor;
